import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { BRAND } from "@/data/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", hotel_name: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact-requests`, { ...form, page_url: window.location.href });
      toast.success("Thank you. Your inquiry has been submitted successfully. Our team will contact you within 24 business hours.");
      setForm({ full_name: "", email: "", phone: "", hotel_name: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Submission failed. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page" className="max-w-7xl mx-auto px-6 md:px-12 py-20">
      <header className="mb-16 max-w-3xl">
        <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Contact Us</p>
        <h1 className="font-serif-display text-5xl lg:text-7xl mt-5 leading-[1.05]">
          Request your <em className="text-[#D4AF37] not-italic font-medium">factory-direct</em> quote.
        </h1>
        <p className="mt-6 text-[#4A4A4A] text-lg leading-relaxed">
          Tell us about your property and what you need. Our team will reach out within 24 hours with custom factory-direct pricing.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
        {/* Form */}
        <form onSubmit={onSubmit} data-testid="contact-form" className="lg:col-span-7 bg-white border border-[#E5E5E5] p-8 md:p-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">Full Name *</label>
              <input data-testid="contact-name" required name="full_name" value={form.full_name} onChange={onChange} className="mt-2 w-full border-b border-[#E5E5E5] focus:border-[#D4AF37] outline-none py-3 bg-transparent" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">Hotel / Property</label>
              <input data-testid="contact-hotel" name="hotel_name" value={form.hotel_name} onChange={onChange} className="mt-2 w-full border-b border-[#E5E5E5] focus:border-[#D4AF37] outline-none py-3 bg-transparent" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">Email *</label>
              <input data-testid="contact-email" type="email" required name="email" value={form.email} onChange={onChange} className="mt-2 w-full border-b border-[#E5E5E5] focus:border-[#D4AF37] outline-none py-3 bg-transparent" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">Phone</label>
              <input data-testid="contact-phone" name="phone" value={form.phone} onChange={onChange} className="mt-2 w-full border-b border-[#E5E5E5] focus:border-[#D4AF37] outline-none py-3 bg-transparent" />
            </div>
          </div>
          <div className="mt-6">
            <label className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">Subject</label>
            <input data-testid="contact-subject" name="subject" placeholder="e.g. New build PTAC + TV package for 80 rooms" value={form.subject} onChange={onChange} className="mt-2 w-full border-b border-[#E5E5E5] focus:border-[#D4AF37] outline-none py-3 bg-transparent" />
          </div>
          <div className="mt-6">
            <label className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">Message *</label>
            <textarea data-testid="contact-message" name="message" required rows={6} value={form.message} onChange={onChange} className="mt-2 w-full border border-[#E5E5E5] focus:border-[#D4AF37] outline-none py-3 px-4 bg-transparent" />
          </div>
          <button data-testid="contact-submit" type="submit" disabled={submitting} className="mt-8 bg-[#D4AF37] hover:bg-[#B5952F] disabled:opacity-60 text-white px-10 py-4 uppercase tracking-[0.2em] text-xs transition-colors">
            {submitting ? "Sending..." : "Send Inquiry"}
          </button>
        </form>

        {/* Info */}
        <aside className="lg:col-span-5 space-y-8">
          <div className="bg-[#1A1A1A] text-white p-8">
            <h3 className="font-serif-display text-2xl mb-6">Talk to a human</h3>
            <ul className="space-y-5">
              <li className="flex items-start gap-4">
                <Phone className="w-5 h-5 text-[#D4AF37] mt-1 shrink-0" />
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">Phone</p>
                  <a href={`tel:${BRAND.phone}`} data-testid="contact-info-phone" className="text-lg hover:text-[#D4AF37]">{BRAND.phone}</a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <Mail className="w-5 h-5 text-[#D4AF37] mt-1 shrink-0" />
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">Email</p>
                  <a href={`mailto:${BRAND.email}`} data-testid="contact-info-email" className="text-base hover:text-[#D4AF37] break-all">{BRAND.email}</a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <MapPin className="w-5 h-5 text-[#D4AF37] mt-1 shrink-0" />
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">Service Area</p>
                  <p>Hotels across all 50 US states</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <Clock className="w-5 h-5 text-[#D4AF37] mt-1 shrink-0" />
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">Hours</p>
                  <p>Mon – Sat · 9:00 AM – 7:00 PM ET</p>
                </div>
              </li>
            </ul>
          </div>

          <div className="bg-white border border-[#D4AF37]/30 p-8">
            <h4 className="font-serif-display text-xl">Bulk & Furniture Projects</h4>
            <p className="text-sm text-[#4A4A4A] mt-3 leading-relaxed">
              Renovating a wing or opening a new property? Our team specializes in coordinating complete bed frame, PTAC, TV, mirror, lighting and furniture packages with brand-approved spec sheets.
            </p>
          </div>
        </aside>
      </div>
    </main>
  );
};

export default ContactPage;
